import { EntityType, Rights } from '@frontend/common';
import { accountIdFromResolvedEntity } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Application } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { TransactionPudoExtendedClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedPudoTransaction } from '@frontend/transaction/types';
import { FormattedMessage } from 'react-intl';

import { MultiEntityButton } from '../../../components/multi-entity-button/multi-entity-button.component';
import { renderCopyableTrackingNumberCell } from '../../../components/object-list-cells/copyable-tracking-number-cell.component';
import { usePlatformModalHost } from '../../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { ApplicationCreateButton } from '../../components/create-button';
import OverviewHeader from '../../header/overview-header';
import { TabFilter } from '../../tab-filter/tab-filter.component';
import { CreateTransactionModal } from '../create-transaction-modal/create-transaction-modal.component';
import { getPudoTransactionStatesForTab } from '../pudo-transaction-tab-states';
import { useTransactions } from './transactions.controller';

const renderCollisCountCell = (obj: { row: { original: ExtendedPudoTransaction } }) => (
    <td className={defaultCellClassName}>{obj.row.original.collis?.length || 1}</td>
);

export const Transactions = () => {
    const viewProps = useTransactions();
    return (
        <div
            key='pudo-transactions'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <FormattedMessage
                        id='feature.transactions'
                        description='The name of the transactions feature'
                        defaultMessage='Transactions'
                    />
                }
                createButton={
                    <ApplicationCreateButton
                        modalId='pudo-create-transaction'
                        renderDefaultModal={() => <CreateTransactionModal formId={viewProps.formId} />}
                        modalOptions={{ type: ModalType.PANE, className: 'max-w-[600px]' }}
                        createPermissions={[{ item: EntityType.TRANSACTION, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: '?' }]}
                        entity={CommonMessage.OBJECTS.TRANSACTION.DEFAULT}
                        functionality={viewProps.functionality}
                        wizardId={viewProps.functionalityWizardId}
                        requiresWizard
                        label={
                            <FormattedMessage
                                id='feature.transactions.create-transaction'
                                defaultMessage='Create transaction'
                            />
                        }
                    />
                }
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['all', 'active', 'completed', 'cancelled', 'failed', 'draft']}
                    displayMap={
                        new Map([
                            [
                                'all',
                                <FormattedMessage
                                    id='feature.transactions.tabs.all'
                                    defaultMessage='All'
                                    key='feature.transactions.tabs.all'
                                />
                            ],
                            [
                                'active',
                                <FormattedMessage
                                    id='feature.transactions.tabs.active'
                                    defaultMessage='Active'
                                    key='feature.transactions.tabs.active'
                                />
                            ],
                            [
                                'completed',
                                <FormattedMessage
                                    id='feature.transactions.tabs.completed'
                                    defaultMessage='Completed'
                                    key='feature.transactions.tabs.completed'
                                />
                            ],
                            [
                                'cancelled',
                                <FormattedMessage
                                    id='feature.transactions.tabs.cancelled'
                                    description='Tab label for cancelled PuDo transactions'
                                    defaultMessage='Cancelled'
                                    key='feature.transactions.tabs.cancelled'
                                />
                            ],
                            [
                                'failed',
                                <FormattedMessage
                                    id='feature.transactions.tabs.failed'
                                    defaultMessage='Failed'
                                    key='feature.transactions.tabs.failed'
                                />
                            ],
                            [
                                'draft',
                                <FormattedMessage
                                    id='feature.transactions.tabs.draft'
                                    description='Tab label for transactions in draft status'
                                    defaultMessage='Draft'
                                    key='feature.transactions.tabs.draft'
                                />
                            ]
                        ])
                    }
                    defaultActive='active'
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />
                <TransactionList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const TransactionList = (viewProps: ReturnType<typeof useTransactions>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    switch (viewProps.activeTab) {
        case 'all':
        case 'draft':
        case 'active':
            return (
                <ReactiveObjectList<ExtendedPudoTransaction>
                    key={viewProps.activeTab}
                    fetch={(arg) =>
                        TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                            account_id: viewProps.currentAccount,
                            state: getPudoTransactionStatesForTab(viewProps.activeTab),
                            application: Application.PUDO,
                            // List view doesn't render packages or PIN-codes.
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'purchase_order'],
                        filterKeys: ['sender_id', 'receiver_id', 'spot_id', 'current_spot_id', 'slot_id', 'after', 'before'],
                        displayName: new Map([
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DESTINATION],
                            [
                                'current_spot_id',
                                <FormattedMessage
                                    key='pudo.transactions.filter.current-spot'
                                    id='pudo.transactions.filter.current-spot'
                                    defaultMessage='Current SPOT'
                                />
                            ],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DESTINATION],
                            ['sender_id', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                            ['receiver_id', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                            ['before', CommonMessage.FORMS.TO_TIMESTAMP],
                            ['after', CommonMessage.FORMS.FROM_TIMESTAMP]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number', 'creation_timestamp', 'state']}
                    columns={{
                        columns: [
                            'tracking_number',
                            'senders',
                            'dropped_off_by',
                            'receivers',
                            'picked_up_by',
                            'state',
                            'spot',
                            'slot',
                            'purchase_order',
                            'collis',
                            'creation_timestamp'
                        ],
                        headerOverwrite: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                            ['dropped_off_by', CommonMessage.OBJECTS.TRANSACTION.DROPPED_OFF_BY],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                            ['picked_up_by', CommonMessage.OBJECTS.TRANSACTION.PICKED_UP_BY],
                            ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS],
                            ['spot', CommonMessage.OBJECTS.SPOT.DEFAULT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['purchase_order', CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER],
                            ['collis', CommonMessage.OBJECTS.PARCEL.PLURAL],
                            ['creation_timestamp', CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            ['collis', renderCollisCountCell],
                            [
                                'senders',
                                (obj) => {
                                    if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.senders[0].entity_id}
                                            className='text-brand-primary align-center truncate p-2 text-xs'>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.senders[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.senders[0].name}
                                                    key={obj.row.original.senders[0].entity_id}
                                                    foreign={obj.row.original.senders[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                                />
                                                {obj.row.original.senders.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.senders.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0].entity_id}
                                            className='text-brand-primary align-center truncate p-2 text-xs'>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.receivers[0].name}
                                                    key={obj.row.original.receivers[0].entity_id}
                                                    foreign={obj.row.original.receivers[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'dropped_off_by',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.dropped_off_by?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.dropped_off_by ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.dropped_off_by.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.dropped_off_by.name}
                                                    key={obj.row.original.dropped_off_by.entity_id}
                                                    foreign={obj.row.original.dropped_off_by.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.dropped_off_by)}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'picked_up_by',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.picked_up_by?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.picked_up_by ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.picked_up_by.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.picked_up_by.name}
                                                    key={obj.row.original.picked_up_by.entity_id}
                                                    foreign={obj.row.original.picked_up_by.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.picked_up_by)}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.id}
                                            className='text-foreground-default truncate p-2 text-sm font-medium'>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id={`${viewProps.activeTab}-transactions-list`}
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.PUDO } })
                    }
                />
            );
        case 'completed':
        case 'cancelled': {
            return (
                <ReactiveObjectList<ExtendedPudoTransaction>
                    key={viewProps.activeTab}
                    fetch={(arg) =>
                        TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                            account_id: viewProps.currentAccount,
                            application: Application.PUDO,
                            // List view doesn't render packages or PIN-codes.
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            state: getPudoTransactionStatesForTab(viewProps.activeTab),
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'purchase_order'],
                        filterKeys: ['sender_id', 'receiver_id', 'spot_id', 'current_spot_id', 'slot_id', 'before', 'after'],
                        displayName: new Map([
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DESTINATION],
                            [
                                'current_spot_id',
                                <FormattedMessage
                                    key='pudo.transactions.filter.current-spot'
                                    id='pudo.transactions.filter.current-spot'
                                    defaultMessage='Current SPOT'
                                />
                            ],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DESTINATION],
                            ['sender_id', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                            ['receiver_id', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                            ['before', CommonMessage.OBJECTS.COMMON_FIELDS.BEFORE],
                            ['after', CommonMessage.OBJECTS.COMMON_FIELDS.AFTER],
                            ['purchase_order', CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number', 'state']}
                    columns={{
                        columns: [
                            'tracking_number',
                            'senders',
                            'dropped_off_by',
                            'receivers',
                            'picked_up_by',
                            'state',
                            'spot',
                            'slot',
                            'purchase_order',
                            'collis',
                            'dropped_off_timestamp',
                            'picked_up_timestamp'
                        ],
                        headerOverwrite: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                            ['dropped_off_by', CommonMessage.OBJECTS.TRANSACTION.DROPPED_OFF_BY],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                            ['picked_up_by', CommonMessage.OBJECTS.TRANSACTION.PICKED_UP_BY],
                            ['spot', CommonMessage.OBJECTS.SPOT.DEFAULT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['purchase_order', CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER],
                            ['collis', CommonMessage.OBJECTS.PARCEL.PLURAL]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            ['collis', renderCollisCountCell],
                            [
                                'senders',
                                (obj) => {
                                    if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.senders[0].entity_id}
                                            className='text-brand-primary align-center truncate p-2 text-xs'>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.senders[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.senders[0].name}
                                                    key={obj.row.original.senders[0].entity_id}
                                                    foreign={obj.row.original.senders[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                                />
                                                {obj.row.original.senders.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.senders.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0].entity_id}
                                            className='text-brand-primary align-center truncate p-2 text-xs'>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.receivers[0].name}
                                                    key={obj.row.original.receivers[0].entity_id}
                                                    foreign={obj.row.original.receivers[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'dropped_off_by',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.dropped_off_by?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.dropped_off_by ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.dropped_off_by.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.dropped_off_by.name}
                                                    key={obj.row.original.dropped_off_by.entity_id}
                                                    foreign={obj.row.original.dropped_off_by.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.dropped_off_by)}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'picked_up_by',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.picked_up_by?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.picked_up_by ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.picked_up_by.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.picked_up_by.name}
                                                    key={obj.row.original.picked_up_by.entity_id}
                                                    foreign={obj.row.original.picked_up_by.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.picked_up_by)}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'state',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.id}
                                            className='text-foreground-default truncate p-2 text-sm font-medium'>
                                            <TransactionStateBadge state={obj.row.original.state} />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id={`${viewProps.activeTab}-transactions-list`}
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.PUDO } })
                    }
                />
            );
        }
        case 'failed':
            return (
                <ReactiveObjectList<ExtendedPudoTransaction>
                    key='failed'
                    fetch={(arg) =>
                        TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                            account_id: viewProps.currentAccount,
                            state: getPudoTransactionStatesForTab('failed'),
                            application: Application.PUDO,
                            // List view doesn't render packages or PIN-codes.
                            without_packages: 'true',
                            without_pin_codes: 'true',
                            ...arg
                        })
                    }
                    cardClassName='card bg-background-surface w-full border border-border-default'
                    dispatch={viewProps.dispatch}
                    activeFilter={{
                        searchKeys: ['tracking_number', 'sender', 'receiver', 'purchase_order'],
                        filterKeys: ['sender_id', 'receiver_id', 'spot_id', 'current_spot_id', 'slot_id', 'waiting'],
                        displayName: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['spot_id', CommonMessage.OBJECTS.SPOT.DESTINATION],
                            [
                                'current_spot_id',
                                <FormattedMessage
                                    key='pudo.transactions.filter.current-spot'
                                    id='pudo.transactions.filter.current-spot'
                                    defaultMessage='Current SPOT'
                                />
                            ],
                            ['slot_id', CommonMessage.OBJECTS.SLOT.DESTINATION],
                            ['sender_id', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                            ['receiver_id', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                            ['purchase_order', CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER],
                            ['sender', CommonMessage.OBJECTS.TRANSACTION.SENDER],
                            ['receiver', CommonMessage.OBJECTS.TRANSACTION.RECEIVER],
                            ['waiting', CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR]
                        ]),
                        ...(viewProps.report && {
                            export: {
                                modalId: viewProps.reportModalId,
                                report: viewProps.report,
                                data: viewProps.exportData
                            }
                        })
                    }}
                    sortingColumns={['tracking_number']}
                    columns={{
                        columns: ['tracking_number', 'senders', 'dropped_off_by', 'receivers', 'picked_up_by', 'spot', 'slot', 'purchase_order', 'collis'],
                        headerOverwrite: new Map([
                            ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER],
                            ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                            ['dropped_off_by', CommonMessage.OBJECTS.TRANSACTION.DROPPED_OFF_BY],
                            ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                            ['picked_up_by', CommonMessage.OBJECTS.TRANSACTION.PICKED_UP_BY],
                            ['spot', CommonMessage.OBJECTS.SPOT.DEFAULT],
                            ['slot', CommonMessage.OBJECTS.SLOT.DEFAULT],
                            ['purchase_order', CommonMessage.OBJECTS.TRANSACTION.PURCHASE_ORDER],
                            ['collis', CommonMessage.OBJECTS.PARCEL.PLURAL]
                        ]),
                        customCells: new Map([
                            ['tracking_number', renderCopyableTrackingNumberCell],
                            ['collis', renderCollisCountCell],
                            [
                                'senders',
                                (obj) => {
                                    if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.senders[0].entity_id}
                                            className='text-brand-primary align-center truncate p-2 text-xs'>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.senders[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.senders[0].name}
                                                    key={obj.row.original.senders[0].entity_id}
                                                    foreign={obj.row.original.senders[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                                />
                                                {obj.row.original.senders.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.senders.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0].entity_id}
                                            className='text-brand-primary align-center truncate p-2 text-xs'>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0].entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.receivers[0].name}
                                                    key={obj.row.original.receivers[0].entity_id}
                                                    foreign={obj.row.original.receivers[0].foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj.entity_id}
                                                                entityType={EntityType.USER}
                                                                display={obj.name}
                                                                key={obj.entity_id}
                                                                foreign={obj.foreign}
                                                                accountId={accountIdFromResolvedEntity(obj)}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            [
                                'dropped_off_by',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.dropped_off_by?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.dropped_off_by ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.dropped_off_by.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.dropped_off_by.name}
                                                    key={obj.row.original.dropped_off_by.entity_id}
                                                    foreign={obj.row.original.dropped_off_by.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.dropped_off_by)}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'picked_up_by',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.picked_up_by?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.picked_up_by ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.picked_up_by.entity_id}
                                                    entityType={EntityType.USER}
                                                    display={obj.row.original.picked_up_by.name}
                                                    key={obj.row.original.picked_up_by.entity_id}
                                                    foreign={obj.row.original.picked_up_by.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.picked_up_by)}
                                                />
                                            ) : (
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            )}
                                        </td>
                                    );
                                }
                            ],
                            [
                                'spot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.spot?.entity_id}
                                            className={defaultCellClassName}>
                                            <PlatformObjectBadge
                                                id={obj.row.original.spot?.entity_id}
                                                entityType={EntityType.SPOT}
                                                display={obj.row.original.spot?.name}
                                                key={obj.row.original.spot?.entity_id}
                                                foreign={obj.row.original.spot?.foreign}
                                                accountId={accountIdFromResolvedEntity(obj.row.original.spot)}
                                            />
                                        </td>
                                    );
                                }
                            ],
                            [
                                'slot',
                                (obj) => {
                                    return (
                                        <td
                                            key={obj.row.original.slot?.entity_id}
                                            className={defaultCellClassName}>
                                            {obj.row.original.slot ? (
                                                <PlatformObjectBadge
                                                    id={obj.row.original.slot?.entity_id}
                                                    entityType={EntityType.SLOT}
                                                    display={obj.row.original.slot?.name}
                                                    key={obj.row.original.slot?.entity_id}
                                                    parentIds={{
                                                        spot_id: obj.row.original.slot.data.spot_id,
                                                        module_id: obj.row.original.slot.data.module_id
                                                    }}
                                                    foreign={obj.row.original.slot?.foreign}
                                                    accountId={accountIdFromResolvedEntity(obj.row.original.slot)}
                                                />
                                            ) : (
                                                <span>/</span>
                                            )}
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.TRANSACTION}
                    eventListener={EntityType.TRANSACTION}
                    showCount
                    id='failed-transactions-list'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({ entityType: EntityType.TRANSACTION, id: obj.id, context: { application: Application.PUDO } })
                    }
                />
            );
    }
};
